import React, { useState, useEffect, useMemo } from 'react';
import styles from './FileExtraction.module.css';
import DropdownSelect, { Option } from '../general-components/DropdownSelect';
import Button, { ButtonType } from '../general-components/Button';
import { IAITask } from '../../models/aiTaskModel';
import FormInput from '../general-components/FormInput';

interface AITaskConfigurationProps {
    aiTasks: IAITask[];
    isExtracting: boolean;
    onExtract: (task: IAITask, formValues: Record<string, any>) => void;
}

const AITaskConfiguration: React.FC<AITaskConfigurationProps> = ({
    aiTasks,
    isExtracting,
    onExtract,
}) => {
    const [selectedTaskId, setSelectedTaskId] = useState<string>('');
    const [formValues, setFormValues] = useState<Record<string, any>>({});

    const selectedTask = aiTasks.find((t) => t._id === selectedTaskId);

    useEffect(() => {
        if (aiTasks.length > 0 && !selectedTaskId) {
            setSelectedTaskId(aiTasks[0]._id);
        }
    }, [aiTasks]);

    // Reset form values when selected task changes
    useEffect(() => {
        if (selectedTask && selectedTask.inputSchema?.properties) {
            const initial: any = {};
            Object.keys(selectedTask.inputSchema.properties).forEach((key) => {
                if (key !== 'file_url') {
                    initial[key] = '';
                }
            });
            setFormValues(initial);
        }
    }, [selectedTask]);

    //Not sure if I understand this AI generated code, but seems to work..
    const taskOptions = useMemo(() => {
        return aiTasks.reduce((acc, task) => {
            const compositeKey = `${task.name}::${task._id}`;
            acc[compositeKey] = {
                checked: task._id === selectedTaskId,
                label: task.name,
            } as Option;
            return acc;
        }, {} as Record<string, Option>);
    }, [aiTasks, selectedTaskId]);

    const handleExtractClick = () => {
        if (selectedTask) {
            onExtract(selectedTask, formValues);
        }
    };

    return (
        <div className={styles.extractionSection}>
            <h6>AI Extraction</h6>
            {aiTasks.length === 0 ? (
                <p className={styles.noFilesMessage} style={{ marginTop: '10px' }}>
                    No extraction tasks available.
                </p>
            ) : (
                <>
                    <div className={styles.taskSelectWrapper}>
                        <DropdownSelect
                            isSearchable={false}
                            isMulti={false}
                            name={selectedTask?.name || 'Select Extraction Task'}
                            labelled={true}
                            options={taskOptions}
                            onChange={(selected: Record<string, Option>) => {
                                const selectedKey = Object.keys(selected).find((key) => selected[key].checked);
                                const selectedId = selectedKey ? selectedKey.split('::').pop() : null;
                                setSelectedTaskId(selectedId || '');
                            }}
                        />
                    </div>

                    {/* Dynamic Inputs based on Schema */}
                    {selectedTask && selectedTask.inputSchema && selectedTask.inputSchema.properties && (
                        <div className={styles.dynamicInputsContainer}>
                            {Object.entries(selectedTask.inputSchema.properties).map(([key, field]: [string, any]) => {
                                if (key === 'file_url') return null;
                                return (
                                    <div key={key}>
                                        {field.type === 'boolean' ? (
                                            <FormInput
                                                tag="select"
                                                label={key}
                                                info={field.description}
                                                name={key}
                                                defaultValue={formValues[key] === undefined ? '' : String(formValues[key])}
                                                onSelect={(e) => {
                                                    const val = e.target.value === '' ? undefined : e.target.value === 'true';
                                                    setFormValues({ ...formValues, [key]: val });
                                                }}
                                            >
                                                <option value="">Select...</option>
                                                <option value="true">True</option>
                                                <option value="false">False</option>
                                            </FormInput>
                                        ) : (
                                            <FormInput
                                                tag="input"
                                                type={field.type === 'number' ? 'number' : 'text'}
                                                label={key}
                                                info={field.description}
                                                name={key}
                                                defaultValue={formValues[key] || ''}
                                                onChange={(e) => setFormValues({ ...formValues, [key]: e.target.value })}
                                                placeholder={`Enter ${key}`}
                                                required={selectedTask.inputSchema.required?.includes(key)}
                                            />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
                        <Button type={ButtonType.primary} onClick={handleExtractClick} loading={isExtracting} disabled={isExtracting || !selectedTaskId}>
                            Auto-fill Form
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
};

export default AITaskConfiguration;